import type { NavbarBranchItem } from "@src/components/board/queries/fetch-ranklist";
import {
	findNavbarBranchItemInCache,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { parseChatDocumentId } from "@src/components/collaboration/document-id";
import { NavbarSection } from "@src/gql/graphql";
import type { QueryClient } from "@tanstack/react-query";
import { generateText } from "ai";
import { createIllumiProvider } from "./illumi-llm-provider";
import { chatQueryKeys } from "./queries/chat-queries";
import type { AgentChatMessage } from "./types";

export const NEW_CHAT_NAME = "New chat";
export const CHAT_NAME_MAX_LENGTH = 40;

type MyChatListItem = {
	id: string;
	name: string;
};

export function chatNameFromMessage(text: string): string {
	const collapsed = text.trim().replace(/\s+/g, " ");
	if (!collapsed) {
		return NEW_CHAT_NAME;
	}
	return collapsed.slice(0, CHAT_NAME_MAX_LENGTH);
}

export function shouldAutoTitleChat(args: {
	alreadyAttempted: boolean;
	currentName: string | undefined;
	seedName: string;
}): boolean {
	if (args.alreadyAttempted) {
		return false;
	}
	if (args.currentName === undefined) {
		return true;
	}
	return args.currentName === args.seedName;
}

export function assistantPlainText(messages: AgentChatMessage[]): string {
	const chunks: string[] = [];
	for (const message of messages) {
		if (message.role !== "assistant") {
			continue;
		}
		for (const part of message.parts) {
			if (part.type === "text" && part.text.trim()) {
				chunks.push(part.text.trim());
			}
		}
	}
	return chunks.join("\n").slice(0, 2000);
}

export function firstUserText(messages: AgentChatMessage[]): string {
	const user = messages.find((message) => message.role === "user");
	return user?.role === "user" ? user.content.trim() : "";
}

export function normalizeGeneratedTitle(raw: string): string | null {
	const cleaned = raw
		.trim()
		.replace(/^["'`]+|["'`]+$/g, "")
		.replace(/\s+/g, " ")
		.replace(/[.!?]+$/g, "")
		.trim();
	if (!cleaned) {
		return null;
	}
	return cleaned.slice(0, CHAT_NAME_MAX_LENGTH);
}

export function resolveCachedChatName(
	queryClient: QueryClient,
	chatId: string,
): string | undefined {
	const chatKey = parseChatDocumentId(chatId).toString();
	const fromNavbar = findNavbarBranchItemInCache(
		queryClient,
		NavbarSection.MyWhiteboards,
		chatKey,
	);
	if (fromNavbar?.name) {
		return fromNavbar.name;
	}

	const myChatQueries = queryClient.getQueriesData<MyChatListItem[]>({
		queryKey: chatQueryKeys.all,
	});
	for (const [, chats] of myChatQueries) {
		if (!chats) {
			continue;
		}
		const match = chats.find(
			(chat) =>
				chat.id === chatId ||
				parseChatDocumentId(chat.id).toString() === chatKey,
		);
		if (match?.name) {
			return match.name;
		}
	}

	const allNavbar = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.all(),
	});
	for (const [, items] of allNavbar) {
		const match = items?.find((item) => item.key === chatKey);
		if (match?.name) {
			return match.name;
		}
	}

	return undefined;
}

export async function generateChatTitle(args: {
	userText: string;
	assistantText: string;
	llmModel: string;
}): Promise<string | null> {
	const userText = args.userText.trim() || "(attachment only)";
	const assistantText = args.assistantText.trim();
	if (!assistantText) {
		return null;
	}

	const provider = await createIllumiProvider();
	const result = await generateText({
		model: provider.chatModel(args.llmModel),
		prompt: [
			"Write a short chat title that captures the user's intent.",
			"Rules: max 6 words, no quotes, no trailing punctuation,",
			"title case preferred, English.",
			`User: ${userText}`,
			`Assistant: ${assistantText}`,
			"Title:",
		].join("\n"),
	});

	return normalizeGeneratedTitle(result.text);
}

export type RenameChatFn = (input: {
	chatId: string;
	name: string;
	isAutoGenerated: boolean;
}) => Promise<unknown>;

/**
 * One-shot auto-title after the first successful reply.
 * Marks the chat as attempted even when generation or rename fails,
 * so later turns never retry.
 */
export async function maybeRenameChatWithGeneratedTitle(args: {
	chatId: string;
	messages: AgentChatMessage[];
	llmModel: string;
	seedName: string | undefined;
	alreadyAttempted: boolean;
	getCurrentName: () => string | undefined;
	renameChat: RenameChatFn;
}): Promise<"skipped" | "renamed" | "failed"> {
	const seedName = args.seedName;
	if (
		!seedName ||
		!shouldAutoTitleChat({
			alreadyAttempted: args.alreadyAttempted,
			currentName: args.getCurrentName(),
			seedName,
		})
	) {
		return "skipped";
	}

	const assistantText = assistantPlainText(args.messages);
	if (!assistantText) {
		return "skipped";
	}

	try {
		const title = await generateChatTitle({
			userText: firstUserText(args.messages),
			assistantText,
			llmModel: args.llmModel,
		});
		if (!title || title === seedName) {
			return "failed";
		}

		const currentName = args.getCurrentName();
		if (
			!shouldAutoTitleChat({
				alreadyAttempted: false,
				currentName,
				seedName,
			})
		) {
			return "skipped";
		}

		await args.renameChat({
			chatId: args.chatId,
			name: title,
			isAutoGenerated: true,
		});
		return "renamed";
	} catch {
		return "failed";
	}
}
